import { useState, useCallback, useRef, useEffect } from 'react';
// @mui
import { styled, Theme } from '@mui/material/styles';

import Stack from '@mui/material/Stack';
import Input from '@mui/material/Input';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
// hooks
import { useLocales } from '@/locales';
// types
import { ISendMessage } from '@/@types/chat';
// utils
import { chatImage } from '@/utils/images';
// components
import Iconify from '@/components/iconify';
import EmojiPicker from '@/components/tribz-emoji-picker';
import MultiFilePreview from '@/components/tribz-upload/MultiFilePreview';
// redux
import { useSelector } from 'react-redux';
import { selectCurrentUser } from '@/redux/slices/auth';
import { usePostUserChatUploadMutation, PostUserChatUploadApiResponse } from '@/redux/coreApi';

// ----------------------------------------------------------------------

const RootStyle = styled('div')(({ theme }: { theme: Theme }) => ({
  minHeight: 56,
  display: 'flex',
  position: 'relative',
  alignItems: 'center',
  paddingLeft: theme.spacing(2),
  borderTop: `solid 1px ${theme.palette.divider}`,
}));

// ----------------------------------------------------------------------

type CoachMessageInputProps = {
  onSend: (data: ISendMessage) => void;
  disabled?: boolean;
};

export default function CoachMessageInput({
  onSend,
  disabled = false,
}: Readonly<CoachMessageInputProps>) {
  const { translate } = useLocales();
  const user = useSelector(selectCurrentUser) ?? {};
  const { id: userId = '' } = user;

  const [attachments, setAttachments] = useState<PostUserChatUploadApiResponse>([]);
  const [message, setMessage] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [interimTranscript, setInterimTranscript] = useState('');

  const fileInputRef = useRef<HTMLInputElement>(null);
  const messageBoxRef = useRef<HTMLInputElement>(null);
  const recognitionRef = useRef<any>(null);
  const recordingTimerRef = useRef<number>();
  const finalTranscriptRef = useRef<string>('');

  const [postUpload] = usePostUserChatUploadMutation();

  const handleAttach = () => {
    fileInputRef.current?.click();
  };

  const handleKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  const handleSend = useCallback(() => {
    if (!message && !attachments.length) {
      return;
    }

    const contentTypes: string[] = ['text'];
    if (attachments.length > 0) {
      const hasImage = attachments.some((a: any) =>
        a.mediaPath?.match(/\.(jpg|jpeg|png|gif|webp)$/i)
      );
      if (hasImage) {
        contentTypes.push('image');
      }
    }

    const newMessage = message.replace(/\n*$/, '');
    onSend({
      message: newMessage,
      contentTypes: contentTypes as any,
      attachments,
      createdAt: new Date().toString(),
      senderId: userId,
    });

    setMessage('');
    setAttachments([]);
    setInterimTranscript('');
  }, [onSend, message, attachments, userId]);

  const changeHandler = async (event: any) => {
    const form = new FormData();
    form.append('file[]', event.target.files[0]);

    const rs: any = await postUpload({ body: form as unknown as { file: Blob[] } });

    if (!rs.error) {
      event.target.value = null;
      setAttachments([...attachments, ...rs.data]);
    }
  };

  const onRemove = (path: string | File) => {
    setAttachments(attachments.filter((a: any) => chatImage(a.mediaPath) !== path));
  };

  const startRecording = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      console.error('Speech recognition not supported in this browser');
      return;
    }

    const SpeechRecognition =
      (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    const recognition = new SpeechRecognition();

    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = navigator.language || 'en-US';

    finalTranscriptRef.current = message;

    recognition.onresult = (event: any) => {
      let interimTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const { transcript } = event.results[i][0];
        if (event.results[i].isFinal) {
          finalTranscriptRef.current += transcript + ' ';
        } else {
          interimTranscript += transcript;
        }
      }

      setMessage(finalTranscriptRef.current + interimTranscript);
      setInterimTranscript(interimTranscript);
    };

    recognition.onerror = (event: any) => {
      console.error('Speech recognition error:', event.error);
      if (event.error === 'no-speech' || event.error === 'audio-capture') {
        stopRecording();
      }
    };

    recognition.onend = () => {
      if (isRecording) {
        // Restart if still recording
        try {
          recognition.start();
        } catch (error) {
          console.error('Error restarting recognition:', error);
          stopRecording();
        }
      }
    };

    recognitionRef.current = recognition;
    setIsRecording(true);
    setRecordingTime(0);
    setInterimTranscript('');
    finalTranscriptRef.current = message;

    try {
      recognition.start();
      recordingTimerRef.current = window.setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } catch (error) {
      console.error('Error starting speech recognition:', error);
      setIsRecording(false);
    }
  };

  const stopRecording = () => {
    if (recognitionRef.current && isRecording) {
      recognitionRef.current.stop();
      setIsRecording(false);
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
      setInterimTranscript('');
      setMessage(finalTranscriptRef.current);
    }
  };

  const cancelRecording = () => {
    if (recognitionRef.current && isRecording) {
      recognitionRef.current.stop();
      setIsRecording(false);
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    }
    setInterimTranscript('');
    setRecordingTime(0);
    finalTranscriptRef.current = message; // Reset to original message
    setMessage(message); // Keep original message
  };

  useEffect(
    () => () => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
      if (recognitionRef.current && isRecording) {
        recognitionRef.current.stop();
      }
    },
    [isRecording]
  );

  const formatTime = (seconds: number) =>
    `${Math.floor(seconds / 60)}:${(seconds % 60).toString().padStart(2, '0')}`;

  return (
    <Stack>
      <RootStyle>
        <Input
          multiline
          maxRows={5}
          inputRef={messageBoxRef}
          disabled={disabled || isRecording}
          fullWidth
          value={message}
          disableUnderline
          onKeyUp={handleKeyUp}
          onChange={(event) => setMessage(event.target.value)}
          placeholder={translate('type_a_message')}
          startAdornment={
            <InputAdornment position="start">
              <EmojiPicker
                disabled={disabled || isRecording}
                value={message}
                setValue={setMessage}
              />
            </InputAdornment>
          }
          endAdornment={
            <Stack direction="row" spacing={1} sx={{ flexShrink: 0, mr: 1.5 }}>
              <IconButton disabled={disabled || isRecording} size="small" onClick={handleAttach}>
                <Iconify icon="eva:attach-2-fill" width={22} height={22} />
              </IconButton>
              {!isRecording ? (
                <IconButton disabled={disabled} size="small" onClick={startRecording} color="error">
                  <Iconify icon="eva:mic-fill" width={22} height={22} />
                </IconButton>
              ) : (
                <IconButton disabled={disabled} size="small" onClick={stopRecording} color="error">
                  <Iconify icon="eva:stop-fill" width={22} height={22} />
                </IconButton>
              )}
            </Stack>
          }
        />

        <Divider orientation="vertical" flexItem />

        <IconButton
          color="primary"
          disabled={disabled || (!message && !attachments.length) || isRecording}
          onClick={handleSend}
          sx={{ mx: 1 }}
        >
          <Iconify icon="ic:round-send" width={22} height={22} />
        </IconButton>

        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={changeHandler}
          accept="image/*,video/*,.pdf,.doc,.docx"
        />
      </RootStyle>

      {isRecording && (
        <Box sx={{ px: 2, py: 1, borderTop: (theme) => `solid 1px ${theme.palette.divider}` }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box sx={{ flexGrow: 1 }}>
              <LinearProgress color="error" />
              {interimTranscript && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0.5, display: 'block' }}
                >
                  {interimTranscript}
                </Typography>
              )}
            </Box>
            <Typography variant="caption" color="error.main">
              {formatTime(recordingTime)}
            </Typography>
            <IconButton size="small" onClick={stopRecording} color="primary">
              <Iconify icon="eva:checkmark-fill" width={18} height={18} />
            </IconButton>
            <IconButton size="small" onClick={cancelRecording} color="error">
              <Iconify icon="eva:close-fill" width={18} height={18} />
            </IconButton>
          </Stack>
        </Box>
      )}

      {!!attachments.length && (
        <Stack
          direction="row"
          sx={{ p: 1, borderTop: (theme) => `solid 1px ${theme.palette.divider}` }}
        >
          <MultiFilePreview
            showPreview
            files={attachments.map((a: any) => chatImage(a.mediaPath))}
            onRemove={onRemove}
          />
        </Stack>
      )}
    </Stack>
  );
}
